import {
  FaroCard,
  FARO_CARD_IMAGE_OVERLAY_CLASS,
} from "@components/common/faro-card/faro-card";
import { ProjectCardBottom } from "@components/common/project-card/project-card-bottom";
import { CardMiddle } from "@components/common/project-card/project-card-middle";
import { ProjectCardTop } from "@components/common/project-card/project-card-top";
import { BaseProjectProps } from "@custom-types/project-types";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { neutral } from "@flat-ui";
import { hasDescribeOnlyPermission } from "@utils/permission-control/project-permission-control";

interface ProjectCardContentProps extends BaseProjectProps {
  /** The field by which the projects are sorted. */
  sortBy: FaroCoreApiTypes.ProjectsSortBy;

  /** Whether the project is currently processing. */
  isProjectProcessing: boolean;

  /** Optional route used when only the thumbnail should be clickable. */
  thumbnailLinkTo?: string;

  /** Called when the thumbnail link is clicked. */
  onThumbnailClick?: () => void;

  /**
   * Suppresses card-wide hover styles when only the thumbnail is interactive.
   * @default false
   */
  shouldSuppressCardHover?: boolean;
}

/**
 * Renders the visual content of a project card: thumbnail, details, and actions.
 */
export function ProjectCardContent({
  project,
  sortBy,
  isProjectProcessing,
  thumbnailLinkTo,
  onThumbnailClick,
  shouldSuppressCardHover = false,
}: ProjectCardContentProps): JSX.Element {
  const isDescribeOnly = hasDescribeOnlyPermission(project);

  return (
    <FaroCard
      isLoading={isProjectProcessing}
      onClick={() => {}}
      sx={{
        background: neutral[0],
        ...(shouldSuppressCardHover && {
          "&:hover": {
            cursor: "default",
            backgroundColor: neutral[0],
            "& .faroCardActions": {
              visibility: isProjectProcessing ? "hidden" : "visible",
            },
            [`& .${FARO_CARD_IMAGE_OVERLAY_CLASS}`]: {
              visibility: "hidden",
            },
          },
          "&:active": {
            background: neutral[0],
          },
        }),
      }}
    >
      {/* Top section: project thumbnail */}
      <ProjectCardTop
        project={project}
        thumbnailLinkTo={thumbnailLinkTo}
        onThumbnailClick={onThumbnailClick}
      />

      {/* Middle section: project details */}
      <CardMiddle project={project} sortBy={sortBy} />

      {/* Bottom section: actions */}
      {!isDescribeOnly && <ProjectCardBottom project={project} />}
    </FaroCard>
  );
}
